import React, { createContext, useContext, useMemo, type ReactNode } from "react";
import type { CanvasInteractionState } from "../domain/selectors";
import { selectionChipLevel } from "../domain/selection/chip-level";
import { selectionEquals } from "../domain/selection/equals";
import type { Selection } from "../domain/types";
import type { ColumnSeamEdgeHighlight, RowEdgeHighlight } from "./emailBuilderSelectionEdgeStrips";

export type CanvasFrameChromeContextValue = CanvasInteractionState & {
  multiSelectRailHostBlockId: string | null;
  multiSelectRailHostRowId: string | null;
  hover: Selection | null;
  previewHover: Selection | null;
  labelStackAnchor: Selection | null;
  columnSeamEdgeHighlight: ColumnSeamEdgeHighlight | null;
  rowEdgeHighlight: RowEdgeHighlight | null;
  setSelection: (selection: Selection) => void;
  setBlockSelection: (blockSelection: {
    selectedBlockIds: string[];
    primaryBlockId: string | null;
    anchorBlockId: string | null;
  }) => void;
  setRowSelection: (rowSelection: {
    selectedRowIds: string[];
    primaryRowId: string | null;
    anchorRowId: string | null;
  }) => void;
  setHover: (selection: Selection | null) => void;
  setColumnSeamEdgeHighlight: (highlight: ColumnSeamEdgeHighlight | null) => void;
  setRowEdgeHighlight: (highlight: RowEdgeHighlight | null) => void;
  setPreviewHover: (selection: Selection) => void;
  clearPreviewHover: () => void;
  openLabelStack: (anchor: Selection) => void;
  closeLabelStack: () => void;
};

const CanvasFrameChromeContext = createContext<CanvasFrameChromeContextValue | null>(
  null,
);

export function CanvasFrameChromeProvider({
  value,
  children,
}: {
  value: CanvasFrameChromeContextValue;
  children: ReactNode;
}) {
  return (
    <CanvasFrameChromeContext.Provider value={value}>
      {children}
    </CanvasFrameChromeContext.Provider>
  );
}

export function useCanvasFrameChrome(): CanvasFrameChromeContextValue {
  const value = useContext(CanvasFrameChromeContext);
  if (value === null) {
    throw new Error("useCanvasFrameChrome must be used within CanvasFrameChromeProvider");
  }
  return value;
}

export function useCanvasFrameSuppressActivation(): boolean {
  return useCanvasFrameChrome().textEditingBlock !== null;
}

export function useCanvasFrameLabelStack(frameSelection: Selection | undefined): {
  stackAnchorGhost: boolean;
  labelPreviewLevel: ReturnType<typeof selectionChipLevel> | null;
  labelStackPinned: boolean;
  onOpenLabelStack: (() => void) | undefined;
  onCloseLabelStack: (() => void) | undefined;
  onPreviewHover: (() => void) | undefined;
  onClearAncestorPreview: (() => void) | undefined;
} {
  const {
    labelStackAnchor,
    previewHover,
    openLabelStack,
    closeLabelStack,
    setPreviewHover,
    clearPreviewHover,
  } = useCanvasFrameChrome();

  return useMemo(() => {
    if (frameSelection === undefined) {
      return {
        stackAnchorGhost: false,
        labelPreviewLevel: null,
        labelStackPinned: false,
        onOpenLabelStack: undefined,
        onCloseLabelStack: undefined,
        onPreviewHover: undefined,
        onClearAncestorPreview: undefined,
      };
    }

    const isLabelStackAnchor =
      labelStackAnchor !== null && selectionEquals(labelStackAnchor, frameSelection);

    return {
      stackAnchorGhost:
        isLabelStackAnchor &&
        previewHover !== null &&
        !selectionEquals(previewHover, frameSelection),
      labelPreviewLevel:
        isLabelStackAnchor && previewHover !== null
          ? selectionChipLevel(previewHover)
          : null,
      labelStackPinned: isLabelStackAnchor,
      onOpenLabelStack: () => openLabelStack(frameSelection),
      onCloseLabelStack: closeLabelStack,
      onPreviewHover: () => setPreviewHover(frameSelection),
      onClearAncestorPreview: clearPreviewHover,
    };
  }, [
    clearPreviewHover,
    closeLabelStack,
    frameSelection,
    labelStackAnchor,
    openLabelStack,
    previewHover,
    setPreviewHover,
  ]);
}

export function useCanvasBlockMultiSelectChrome(blockId: string | null): {
  multiSelectActive: boolean;
  multiSelectRailHost: boolean;
  multiSelectPrimary: boolean;
} {
  const {
    selectedBlockIds,
    primaryBlockId,
    multiSelectRailHostBlockId,
  } = useCanvasFrameChrome();

  return useMemo(() => {
    if (blockId === null) {
      return {
        multiSelectActive: false,
        multiSelectRailHost: false,
        multiSelectPrimary: false,
      };
    }

    const inBlockSelection = selectedBlockIds.includes(blockId);
    const multiSelectMode = selectedBlockIds.length > 1;

    return {
      multiSelectActive: multiSelectMode && inBlockSelection,
      multiSelectRailHost:
        multiSelectMode && multiSelectRailHostBlockId === blockId,
      multiSelectPrimary: multiSelectMode && primaryBlockId === blockId,
    };
  }, [
    blockId,
    multiSelectRailHostBlockId,
    primaryBlockId,
    selectedBlockIds,
  ]);
}

export function useCanvasRowMultiSelectChrome(
  rowId: string | null,
): {
  rowMultiSelectActive: boolean;
  rowMultiSelectMode: boolean;
  rowMultiSelectRailHost: boolean;
  rowMultiSelectPrimary: boolean;
} {
  const {
    selectedRowIds,
    primaryRowId,
    multiSelectRailHostRowId,
  } = useCanvasFrameChrome();

  return useMemo(() => {
    if (rowId === null) {
      return {
        rowMultiSelectActive: false,
        rowMultiSelectMode: false,
        rowMultiSelectRailHost: false,
        rowMultiSelectPrimary: false,
      };
    }

    const inRowSelection = selectedRowIds.includes(rowId);
    const rowMultiSelectMode = selectedRowIds.length > 1;

    return {
      rowMultiSelectActive: rowMultiSelectMode && inRowSelection,
      rowMultiSelectMode,
      rowMultiSelectRailHost:
        rowMultiSelectMode && multiSelectRailHostRowId === rowId,
      rowMultiSelectPrimary: rowMultiSelectMode && primaryRowId === rowId,
    };
  }, [
    multiSelectRailHostRowId,
    primaryRowId,
    rowId,
    selectedRowIds,
  ]);
}
